import { Combinations } from '@givebacks/utils/types';
import { useQuery } from '@tanstack/react-query';

import { SearchSettings, createQueryOptions } from '../../../query';
import { useBackerApi } from '../../api';
import { backerKeys } from '../../query-key-factory';
import {
  ConsumerFamiliesResponse,
  ConsumerFamily,
  ConsumerFamilyCauseResponse,
} from './families.types';

const useListConsumerFamilies = ({
  include,
}: { include?: Combinations<'backers' | 'children', ','> } = {}) => {
  const BackerApi = useBackerApi();

  const getConsumerFamilies = async () => {
    const queryParams = new URLSearchParams();
    if (include) {
      queryParams.set('include', include);
    }
    const { data } = await BackerApi.get<ConsumerFamiliesResponse>(
      `/consumer/families?${queryParams.toString()}`
    );
    return data.families;
  };

  const key = backerKeys.consumer.families.all;

  return useQuery({
    queryFn: getConsumerFamilies,
    queryKey: [...key, include],
  });
};

const useShowConsumerFamily = <T = ConsumerFamily>({
  familyId,
  include,
  select,
}: {
  familyId?: string;
  include?: Combinations<'backers' | 'children', ','>;
  select?: (data: ConsumerFamily) => T;
}) => {
  const BackerApi = useBackerApi();
  const getConsumerFamily = async () => {
    const queryParams = new URLSearchParams();
    if (include) {
      queryParams.set('include', include);
    }
    const { data } = await BackerApi.get<{ family: ConsumerFamily }>(
      `/consumer/families/${familyId}?${queryParams.toString()}`
    );
    return data.family;
  };

  const key = familyId ? backerKeys.consumer.families.id(familyId).all : [];

  return useQuery({
    queryKey: [...key, include],
    queryFn: getConsumerFamily,
    enabled: !!familyId,
    select,
  });
};

const useListConsumerFamilyCauses = ({
  familyId,
  queryOptions,
}: {
  familyId?: string;
  queryOptions?: SearchSettings;
}) => {
  const BackerApi = useBackerApi();

  const getConsumerFamilyCauses = async () => {
    const { queryString } = createQueryOptions({
      search: queryOptions,
      withPagination: false,
    });

    const { data } = await BackerApi.get<ConsumerFamilyCauseResponse>(
      `/consumer/families/${familyId}/causes?${queryString}`
    );
    return data.causes;
  };

  const key = familyId ? backerKeys.consumer.families.id(familyId).causes : [];
  return useQuery({
    queryKey: [...key, queryOptions],
    queryFn: getConsumerFamilyCauses,
    enabled: !!familyId,
  });
};

export { useListConsumerFamilies, useListConsumerFamilyCauses, useShowConsumerFamily };
